import * as React from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { X } from "lucide-react"
import { PublicHero, PublicShell } from "@/components/PublicShell"
import { PublicMeta } from "@/components/public/PublicMeta"
import {
  EstimateLine,
  MinimumProgress,
  bottles,
  removeLine,
  setLineCount,
  shortfallCopy,
} from "@/components/public/SelectionTray"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { QuantityStepper } from "@/components/ui/quantity-stepper"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { formatPence } from "@/lib/money"
import { cn } from "@/lib/utils"
import type { PageProps, Selection, SelectionLine } from "@/types/inertia"
import "@/styles/public-order.css"

const TITLE = "Review Your Selection"
const DESCRIPTION =
  "Review the South African wines in your selection, adjust the bottles and send an order enquiry. We confirm the final quote at consultation."

// Values match Enquiry::EVENT_TYPES (underscored), labels match the occasion pages.
const EVENT_TYPES = [
  { value: "corporate", label: "Corporate" },
  { value: "weddings", label: "Weddings" },
  { value: "hospitality", label: "Hospitality" },
  { value: "private_events", label: "Private Events" },
  { value: "sporting_events", label: "Sporting Events" },
  { value: "other", label: "Something else" },
]

// Private Cellar field treatment lives in the design system
// (design-system.css: .field-label, .form-control-cellar).
const FIELD = "form-control-cellar"

// A label mock-up from the studio that can ride along with this enquiry
// (OrdersController#new: the account's own plus this session's guest designs).
type AttachableMockup = {
  id: number
  title: string
  preview_url: string | null
  bottle_type: "wine" | "mcc"
}

type OrderProps = {
  prefill: { name: string; email: string }
  mockups: AttachableMockup[]
}

type FormData = {
  name: string
  company: string
  phone: string
  email: string
  event_type: string
  event_date: string
  message: string
  website: string
}

export default function Order() {
  const { props } = usePage<PageProps<OrderProps>>()
  const selection = props.selection
  const empty = selection.lines.length === 0
  const mockups = props.mockups ?? []
  // Every design is attached unless the visitor unticks it.
  const [attachedIds, setAttachedIds] = React.useState<number[]>(() => mockups.map((mockup) => mockup.id))

  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />

      <PublicHero
        eyebrow="Your selection"
        title={
          <>
            Review your <span className="soft">selection</span>
          </>
        }
        lede="Adjust the bottles, mix the wines as you like, then send us your details. We will confirm the final quote and the label with you at consultation."
      />

      <section className="band band-charcoal pt-0" aria-label="Your selection">
        <div className="shell">
          {empty ? (
            <EmptySelection />
          ) : (
            <>
              <SelectionLines selection={selection} />
              <LabelDesigns mockups={mockups} attachedIds={attachedIds} onChange={setAttachedIds} />
              <OrderForm
                selection={selection}
                prefill={props.prefill}
                attachedIds={attachedIds}
                baseError={props.errors?.base}
              />
            </>
          )}
        </div>
      </section>
    </PublicShell>
  )
}

function EmptySelection() {
  return (
    <div className="ord-empty">
      <div className="eyebrow eyebrow-center">Nothing chosen yet</div>
      <h2>Your selection is empty</h2>
      <p className="mt-4">
        Browse the range or let the finder suggest wines for your budget and taste. Our minimum is
        100 bottles, mixed as you like.
      </p>
      <div className="ord-empty-actions">
        <Button asChild variant="gold" size="lg">
          <Link href="/wines">View Our Wines</Link>
        </Button>
        <Button asChild variant="ghost-hairline" size="lg">
          <Link href="/finder">Find your wine</Link>
        </Button>
      </div>
    </div>
  )
}

function SelectionLines({ selection }: { selection: Selection }) {
  return (
    <div>
      <div className="ord-section-head">
        <h2>The wines</h2>
        <Link href="/wines">Add more wines</Link>
      </div>

      <table className="ord-table">
        <caption className="sr-only">Wines in your selection</caption>
        <thead>
          <tr>
            <th scope="col">Wine</th>
            <th scope="col">Bottles</th>
            <th scope="col" className="ord-num">
              Per bottle
            </th>
            <th scope="col" className="ord-num">
              Line total
            </th>
            <th scope="col">
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {selection.lines.map((line) => (
            <LineRow key={line.wine_id} line={line} />
          ))}
        </tbody>
      </table>

      <div className="ord-summary">
        <div>
          <div className="ord-overline">Total</div>
          <div className="ord-summary-count mt-2">{bottles(selection.bottle_count)}</div>
        </div>
        <MinimumProgress selection={selection} />
        <EstimateLine pence={selection.estimated_total_pence} />
      </div>
    </div>
  )
}

function LineRow({ line }: { line: SelectionLine }) {
  const priced = line.unit_price_pence !== null
  return (
    <tr>
      <td className="ord-cell-wine">
        <div className="ord-wine">
          <div className="ord-thumb" aria-hidden="true">
            {line.image_url && <img src={line.image_url} alt="" loading="lazy" />}
          </div>
          <div className="min-w-0">
            <span className="ord-wine-name">{line.name}</span>
            <span className="ord-wine-meta">
              {line.producer} · {line.wine_type_label}
              {/* Narrow screens hide the per-bottle column; keep the price in reach. */}
              <span className="md:hidden">
                {" · "}
                {priced ? `${formatPence(line.unit_price_pence!)} per bottle` : "Price on request"}
              </span>
            </span>
          </div>
        </div>
      </td>
      <td className="ord-cell-qty">
        <QuantityStepper
          size="sm"
          value={line.bottle_count}
          label={`Bottles of ${line.name}`}
          onChange={(next) => setLineCount(line.wine_id, next)}
        />
      </td>
      <td className="ord-cell-unit ord-num">
        {priced ? (
          <span className="ord-price">{formatPence(line.unit_price_pence!)}</span>
        ) : (
          <span className="ord-on-request">Price on request</span>
        )}
      </td>
      <td className="ord-cell-total ord-num">
        {priced ? (
          <span className="ord-price">{formatPence(line.unit_price_pence! * line.bottle_count)}</span>
        ) : (
          <span className="ord-on-request">on request</span>
        )}
      </td>
      <td className="ord-cell-remove ord-num">
        <button
          type="button"
          className="sel-remove"
          aria-label={`Remove ${line.name}`}
          onClick={() => removeLine(line.wine_id)}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </td>
    </tr>
  )
}

function LabelDesigns({
  mockups,
  attachedIds,
  onChange,
}: {
  mockups: AttachableMockup[]
  attachedIds: number[]
  onChange: (ids: number[]) => void
}) {
  const toggle = (id: number, checked: boolean) =>
    onChange(checked ? [...attachedIds, id] : attachedIds.filter((attachedId) => attachedId !== id))

  if (mockups.length === 0) {
    return (
      <p className="ord-submit-note mt-16">
        Have a label in mind? <Link href="/studio">Design a label</Link> in our studio and it can
        travel with your enquiry.
      </p>
    )
  }

  return (
    <section className="mt-20" aria-labelledby="order-designs-title">
      <div className="ord-section-head">
        <h2 id="order-designs-title">Your label designs</h2>
        <Button asChild variant="ghost-hairline" size="sm">
          <Link href="/studio">Design a label</Link>
        </Button>
      </div>
      <p className="mb-8 max-w-[60ch]">
        Ticked designs are attached to your enquiry, so we can look at them with you at
        consultation. Every label is subject to graphic design approval by our team.
      </p>

      <ul className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
        {mockups.map((mockup) => {
          const id = `mockup-${mockup.id}`
          const checked = attachedIds.includes(mockup.id)
          return (
            <li key={mockup.id} className="min-w-0">
              <label
                htmlFor={id}
                className={cn(
                  "block cursor-pointer border bg-surface p-3 font-normal text-ink-body transition-colors",
                  checked ? "border-accent" : "border-hairline-soft",
                )}
              >
                <span className="flex aspect-[9/16] items-center justify-center overflow-hidden bg-page">
                  {mockup.preview_url ? (
                    <img
                      src={mockup.preview_url}
                      alt={`Bottle mock-up: ${mockup.title}`}
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="ord-on-request px-3 text-center">Preview to follow</span>
                  )}
                </span>
                <span className="mt-3 flex items-start gap-3">
                  <Checkbox
                    id={id}
                    className="mt-1"
                    checked={checked}
                    onChange={(event) => toggle(mockup.id, event.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="block">{mockup.title}</span>
                    <span className="ord-wine-meta">{checked ? "Attached" : "Not attached"}</span>
                  </span>
                </span>
              </label>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function OrderForm({
  selection,
  prefill,
  attachedIds,
  baseError,
}: {
  selection: Selection
  prefill: OrderProps["prefill"]
  attachedIds: number[]
  baseError?: string
}) {
  const form = useForm<FormData>({
    name: prefill.name,
    company: "",
    phone: "",
    email: prefill.email,
    event_type: "",
    event_date: "",
    message: "",
    website: "",
  })

  const errors = form.errors as Partial<Record<keyof FormData | "base", string>>
  const fieldErrors = Object.keys(errors).filter((key) => key !== "base").length > 0
  const shortfall = shortfallCopy(selection)
  const blocked = !selection.minimum_met

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (blocked) return
    form.transform((data) => ({ ...data, label_mockup_ids: attachedIds }))
    form.post("/order", { preserveScroll: false })
  }

  const text =
    (field: keyof FormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      form.setData(field, event.target.value)

  return (
    <form onSubmit={submit} className="mt-24 max-w-[820px]" aria-labelledby="order-form-title">
      <div className="eyebrow">Send your enquiry</div>
      <h2 id="order-form-title">Your details</h2>
      <p className="mb-10 mt-4 max-w-[56ch]">
        Tell us who you are and what the wine is for. Fields marked with an asterisk are required;
        everything else can wait for our conversation.
      </p>

      {(fieldErrors || baseError) && (
        <div role="alert" className="mb-10 border border-danger/60 bg-danger-faded px-5 py-4">
          <span className="text-danger-display">
            {baseError ?? "Please check the highlighted fields and try again."}
          </span>
        </div>
      )}

      <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
        <Field id="name" label="Name *" error={errors.name}>
          <Input
            id="name"
            autoComplete="name"
            required
            value={form.data.name}
            onChange={text("name")}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "name-error" : undefined}
            className={FIELD}
          />
        </Field>

        <Field id="company" label="Company" hint="Optional" error={errors.company}>
          <Input
            id="company"
            autoComplete="organization"
            value={form.data.company}
            onChange={text("company")}
            className={FIELD}
          />
        </Field>

        <Field id="email" label="Email *" error={errors.email}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={form.data.email}
            onChange={text("email")}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "email-error" : undefined}
            className={FIELD}
          />
        </Field>

        <Field id="phone" label="Phone" error={errors.phone}>
          <Input
            id="phone"
            type="tel"
            autoComplete="tel"
            value={form.data.phone}
            onChange={text("phone")}
            className={FIELD}
          />
        </Field>

        <Field id="event_type" label="Occasion *" error={errors.event_type}>
          <Select
            id="event_type"
            required
            value={form.data.event_type}
            onChange={text("event_type")}
            aria-invalid={Boolean(errors.event_type)}
            aria-describedby={errors.event_type ? "event_type-error" : undefined}
            className={FIELD}
          >
            <option value="" disabled>
              Choose an occasion
            </option>
            {EVENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field id="event_date" label="Date required" error={errors.event_date}>
          <Input
            id="event_date"
            type="date"
            value={form.data.event_date}
            onChange={text("event_date")}
            className={FIELD}
          />
        </Field>

        <Field id="message" label="Anything else we should know" className="sm:col-span-2" error={errors.message}>
          <Textarea
            id="message"
            rows={5}
            placeholder="The occasion, the guests, delivery timing, any label ideas."
            value={form.data.message}
            onChange={text("message")}
            className={FIELD}
          />
        </Field>

        {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
        <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.data.website}
            onChange={text("website")}
          />
        </div>
      </div>

      <div className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center">
        <Button
          type="submit"
          variant="gold"
          size="lg"
          disabled={blocked || form.processing}
          aria-describedby="order-submit-note"
        >
          {form.processing ? "Sending…" : "Send my order enquiry"}
        </Button>
        <div id="order-submit-note" className="ord-submit-note">
          {shortfall ? (
            <>
              <strong>{shortfall}</strong> to send your enquiry.
            </>
          ) : (
            <>
              No payment now — we confirm the final quote at consultation. See our{" "}
              <Link href="/privacy">privacy policy</Link>.
            </>
          )}
        </div>
      </div>
    </form>
  )
}

function Field({
  id,
  label,
  hint,
  error,
  className,
  children,
}: {
  id: string
  label: string
  hint?: string
  error?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children}
      {error ? (
        <div id={`${id}-error`} className="field-error">
          {error}
        </div>
      ) : (
        hint && (
          <div id={`${id}-hint`} className="field-hint">
            {hint}
          </div>
        )
      )}
    </div>
  )
}
